import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  IconButton,
  Stack,
  InputAdornment,
  Tooltip,
  Chip,
} from "@mui/material";
import { Close as CloseIcon, CameraAlt as CameraIcon, Warning as WarningIcon } from "@mui/icons-material";

export interface FormField {
  name: string;
  /** Logical key in form state; use inputName for a distinct HTML name (avoids browser autofill collisions). */
  inputName?: string;
  label: string;
  type?: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  autoComplete?: string;
  aiSuggestion?: boolean;
  aiConfidence?: number;
  customComponent?: (props: {
    value: any;
    onChange: (value: any) => void;
    error?: boolean;
    helperText?: string;
    label: string;
    required?: boolean;
    disabled?: boolean;
  }) => React.ReactNode;
}

interface CustomFormDialogProps<T> {
  open: boolean;
  onClose: () => void;
  onSubmit: (formData: T) => void;
  title: string;
  submitButtonText?: string;
  cancelButtonText?: string;
  formFields: FormField[];
  initialData?: T;
  validate?: (data: T) => Record<string, string>;
  fullWidth?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  customContent?: React.ReactNode;
  onFormDataChange?: (formData: T) => void;
}

function CustomFormDialog<T extends Record<string, any>>({
  open,
  onClose,
  onSubmit,
  title,
  submitButtonText = "Zapisz",
  cancelButtonText = "Anuluj",
  formFields,
  initialData = {} as T,
  validate,
  fullWidth = true,
  maxWidth = "sm",
  customContent,
  onFormDataChange,
}: CustomFormDialogProps<T>) {
  const [formData, setFormData] = React.useState<T>(initialData);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const prevOpenRef = React.useRef(open);
  const formDataRef = React.useRef<T>(formData);
  formDataRef.current = formData;

  // Reset form data only when dialog opens (not when initialData reference changes while open).
  // When open, parent often passes inline initialData → new ref on every re-render → would overwrite user input.
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setFormData(initialData);
      setErrors({});
    }
    prevOpenRef.current = open;
  }, [open, initialData]);

  const resolveFieldName = (htmlName: string) => {
    const fieldDef = formFields.find(
      (f) => (f.inputName ?? f.name) === htmlName,
    );
    return fieldDef?.name ?? htmlName;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const name = resolveFieldName(e.target.name);
    const { value } = e.target;
    setFormData((prev) => {
      const newData = {
        ...prev,
        [name]: value,
      };
      if (onFormDataChange) {
        onFormDataChange(newData);
      }
      return newData;
    });

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate) {
      const validationErrors = validate(formDataRef.current);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    }

    onSubmit(formDataRef.current);
  };

  const isFormValid =
    formFields.every((field) => {
      if (field.required) {
        const value = formData[field.name];
        if (Array.isArray(value)) return value.length > 0;
        return value !== undefined && value !== "";
      }
      return true;
    }) && Object.values(errors).every((error) => !error);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={maxWidth === false}
      PaperProps={{
        sx: {
          borderRadius: "11px",
          minWidth: 400,
          background: "#2a2a2a",
          border: "1px solid #474747ff",
        },
      }}
    >
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" component="div" fontWeight={600}>
              {title}
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
              sx={{
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              pt: 2,
              color: "text.primary",
            }}
          >
            {formFields.map((field) => {
              const hasAISuggestion = field.aiSuggestion === true;
              const confidence = field.aiConfidence || 0;
              const showWarning = hasAISuggestion && confidence < 0.4;

              // If field has customComponent, render it instead of TextField
              if (field.customComponent) {
                return (
                  <Box key={field.name}>
                    {field.customComponent({
                      value: formData[field.name] || null,
                      onChange: (value: any) => {
                        setFormData((prev) => {
                          const newData = {
                            ...prev,
                            [field.name]: value,
                          };
                          if (onFormDataChange) {
                            onFormDataChange(newData);
                          }
                          return newData;
                        });
                        if (errors[field.name]) {
                          setErrors((prev) => ({
                            ...prev,
                            [field.name]: "",
                          }));
                        }
                      },
                      error: !!errors[field.name],
                      helperText: errors[field.name],
                      label: field.label,
                      required: field.required,
                    })}
                  </Box>
                );
              }

              return (
                <TextField
                  key={field.name}
                  name={field.inputName ?? field.name}
                  label={field.label}
                  type={field.type || "text"}
                  required={field.required}
                  value={formData[field.name] || ""}
                  onChange={handleInputChange}
                  autoComplete={field.autoComplete}
                  error={!!errors[field.name]}
                  helperText={
                    errors[field.name] ||
                    (hasAISuggestion && (
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CameraIcon fontSize="small" sx={{ fontSize: 14 }} />
                        <span>Sugestia z AI</span>
                        {confidence > 0 && (
                          <Chip
                            label={`${Math.round(confidence * 100)}%`}
                            size="small"
                            color={confidence > 0.7 ? 'success' : confidence > 0.4 ? 'warning' : 'error'}
                            sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                          />
                        )}
                      </Box>
                    ))
                  }
                  fullWidth
                  margin="normal"
                  multiline={field.multiline}
                  rows={field.rows}
                  InputProps={{
                    ...(field.type === "date" ? {
                      onClick: (e: React.MouseEvent<HTMLElement>) => {
                        const input = (e.currentTarget as HTMLElement).querySelector('input[type="date"]') as HTMLInputElement | null;
                        input?.showPicker?.();
                        input?.focus();
                      },
                    } : {}),
                    endAdornment: hasAISuggestion ? (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            showWarning
                              ? "Niska pewność rozpoznania - wymagana ręczna weryfikacja"
                              : `Sugestia z AI (pewność: ${Math.round(confidence * 100)}%) - możesz edytować`
                          }
                        >
                          {showWarning ? (
                            <WarningIcon
                              fontSize="small"
                              color="warning"
                              sx={{ cursor: 'help' }}
                            />
                          ) : (
                            <CameraIcon
                              fontSize="small"
                              color="primary"
                              sx={{ cursor: 'help' }}
                            />
                          )}
                        </Tooltip>
                      </InputAdornment>
                    ) : undefined,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: hasAISuggestion && showWarning ? "warning.main" : "#474747ff",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(255, 255, 255, 0.5)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "text.secondary",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "text.primary",
                    },
                    ...(field.type === "date" ? {
                      "& .MuiOutlinedInput-root": {
                        cursor: "pointer",
                      },
                      "& .MuiInputBase-input": {
                        cursor: "pointer",
                      },
                      "& .MuiInputBase-input::-webkit-calendar-picker-indicator": {
                        filter: "invert(1)",
                        cursor: "pointer",
                        opacity: 0.9,
                      },
                    } : {}),
                  }}
                />
              );
            })}
            {customContent && (
              <Box sx={{ mt: 2 }}>
                {customContent}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            sx={{
              minWidth: 100,
              borderColor: "#474747ff",
              color: "text.primary",
              "&:hover": {
                borderColor: "rgba(255, 255, 255, 0.5)",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
              },
            }}
          >
            {cancelButtonText}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!isFormValid}
            sx={{
              minWidth: 100,
              "&:disabled": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
                color: "rgba(255, 255, 255, 0.3)",
              },
            }}
          >
            {submitButtonText}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default CustomFormDialog;
